/**
 * Redux Reducer
 * Handles state updates for the questionnaire application
 */

import { ActionTypes, ChildInfoData, UserExampleAudio } from "./Action";

// Hierarchical question structure
interface QuestionNode {
  id: string;
  question?: string;
  answer: "yes" | "no" | null;
  result?: "pass" | "fail";
  completed?: boolean;
  userExample?: string;
  noExamplesChecked?: boolean;
  examplesSaved?: boolean;
  userExampleAudio?: UserExampleAudio;
  children?: QuestionNode[];
}

// State interface for the answers slice
interface AnswersState {
  questions: { [key: string]: QuestionNode };
  childInfo: ChildInfoData | null;
}

// Initial state
const initialState: AnswersState = {
  questions: {},
  childInfo: null,
};

/**
 * Helper function to find a question node by ID in the hierarchical structure
 */
const findQuestionNode = (questions: { [key: string]: QuestionNode }, questionId: string): QuestionNode | null => {
  const parts = questionId.split('.');
  let current: QuestionNode | undefined = questions[parts[0]];
  
  if (!current) return null;
  
  for (let i = 1; i < parts.length; i++) {
    if (!current.children) return null;
    current = current.children.find(child => child.id === parts.slice(0, i + 1).join('.'));
    if (!current) return null;
  }
  
  return current;
};

/**
 * Helper function to update a question node by ID in the hierarchical structure
 */
const updateQuestionNode = (questions: { [key: string]: QuestionNode }, questionId: string, updates: Partial<QuestionNode>): { [key: string]: QuestionNode } => {
  const parts = questionId.split('.');
  const newQuestions = { ...questions };
  
  if (parts.length === 1) {
    // Root level question
    newQuestions[parts[0]] = { ...newQuestions[parts[0]], ...updates };
    return newQuestions;
  }
  
  // Nested question
  let current = newQuestions[parts[0]];
  if (!current) {
    current = { id: parts[0], answer: null };
    newQuestions[parts[0]] = current;
  }
  
  let parent = current;
  for (let i = 1; i < parts.length - 1; i++) {
    const currentId = parts.slice(0, i + 1).join('.');
    if (!parent.children) parent.children = [];
    
    let child = parent.children.find(c => c.id === currentId);
    if (!child) {
      child = { id: currentId, answer: null };
      parent.children.push(child);
    }
    parent = child;
  }
  
  const targetId = parts.slice(0, parts.length - 1).join('.');
  if (!parent.children) parent.children = [];
  
  const targetIndex = parent.children.findIndex(c => c.id === targetId);
  if (targetIndex >= 0) {
    parent.children[targetIndex] = { ...parent.children[targetIndex], ...updates };
  } else {
    parent.children.push({ id: targetId, answer: null, ...updates });
  }
  
  return newQuestions;
};

/**
 * Main reducer function for handling answer-related actions
 * @param state - Current state
 * @param action - Action to process
 * @returns Updated state
 */
const handleAnswers = (state: AnswersState = initialState, action: ActionTypes): AnswersState => {
  switch (action.type) {
    case "SAVE_QUESTION_RESULT":
      // Convert legacy format to new hierarchical format
      const questionId = `Q${action.payload.questionId}`;
      const questionNode: QuestionNode = {
        id: questionId,
        answer: action.payload.mainAnswer,
        result: action.payload.result,
        completed: true,
        userExample: action.payload.userExample,
        noExamplesChecked: action.payload.noExamplesChecked,
        examplesSaved: action.payload.examplesSaved,
        userExampleAudio: action.payload.userExampleAudio,
        children: action.payload.subAnswers.map((answer, index) => ({
          id: `${questionId}.${index + 1}`,
          answer: answer as "yes" | "no" | null
        }))
      };
      
      return {
        ...state,
        questions: {
          ...state.questions,
          [questionId]: questionNode
        }
      };
      
    case "SAVE_COMPLEX_QUESTION_RESULT":
      // Convert complex data to hierarchical format
      const complexQuestionId = `Q${action.payload.questionId}`;
      const complexData = action.payload.complexData;
      
      let newQuestions = { ...state.questions };
      
      // Create root question node
      const rootNode: QuestionNode = {
        id: complexQuestionId,
        answer: complexData[complexQuestionId] || null,
        result: action.payload.result,
        completed: true,
        userExample: complexData.userExample,
        noExamplesChecked: complexData.noExamplesChecked,
        examplesSaved: complexData.examplesSaved,
        userExampleAudio: complexData.userExampleAudio,
        children: []
      };
      
      // Add children based on complex data
      Object.keys(complexData).forEach(key => {
        if (key.startsWith(complexQuestionId + '.') && key !== complexQuestionId) {
          const childId = key;
          const childAnswer = complexData[key];
          
          if (childAnswer !== undefined) {
            const childNode: QuestionNode = {
              id: childId,
              answer: childAnswer
            };
            
            // Handle nested children (e.g., Q20.2.1, Q20.2.2)
            const parts = childId.split('.');
            if (parts.length === 3) {
              // This is a nested child (e.g., Q20.2.1)
              const parentId = parts.slice(0, 2).join('.');
              
              // Find or create parent
              let parent = rootNode.children?.find(c => c.id === parentId);
              if (!parent) {
                parent = { id: parentId, answer: null, children: [] };
                rootNode.children!.push(parent);
              }
              
              if (!parent.children) parent.children = [];
              parent.children.push(childNode);
            } else {
              // Direct child (e.g., Q20.1)
              rootNode.children!.push(childNode);
            }
          }
        }
      });
      
      return {
        ...state,
        questions: {
          ...state.questions,
          [complexQuestionId]: rootNode
        }
      };
      
    case "CLEAR_QUESTION_RESULT":
      const questionToClear = `Q${action.payload.questionId}`;
      const { [questionToClear]: removed, ...remainingQuestions } = state.questions;
      
      return {
        ...state,
        questions: remainingQuestions
      };
      
    case "SET_QUESTION_COMPLETED":
      const questionToUpdate = `Q${action.payload.questionId}`;
      const existingQuestion = state.questions[questionToUpdate];
      
      if (existingQuestion) {
        return {
          ...state,
          questions: {
            ...state.questions,
            [questionToUpdate]: {
              ...existingQuestion,
              completed: action.payload.completed
            }
          }
        };
      }
      
      return state;
      
    case "SAVE_CHILD_INFO":
      return {
        ...state,
        childInfo: action.payload,
      };
      
    case "CLEAR_CHILD_INFO":
      return {
        ...state,
        childInfo: null,
      };
      
    case "CLEAR_ALL_DATA":
      // Clear localStorage when clearing all data
      try {
        localStorage.removeItem('mchat-assessment-data');
      } catch (err) {
        // Ignore errors
      }
      return {
        ...initialState,
      };
      
    default:
      return state;
  }
};

export default handleAnswers;